import { useContext, useEffect, useState } from 'react'
import { FullStory } from '@fullstory/browser'
import { EmailOutlined, PhoneOutlined, QuestionAnswerOutlined } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, Link, Typography } from '@mui/material'
import { useBooleanFlagValue, useWhenProviderReady } from '@openfeature/react-sdk'

import { ChatContext } from '../../providers/chat-provider'
import { HelpCategory } from '../../routes/alert-submit/help-content'
import {
  load8x8Chat,
  UNAUTHENTICATED_CHAT_BUTTON_CONTAINER_ID,
  UNAUTHENTICATED_CHAT_CHANNEL_NAME,
  UNAUTHENTICATED_CHAT_CHANNEL_UUID,
  UNAUTHENTICATED_CHAT_SCRIPT_UUID,
  waitFor8x8ChatButton,
} from '../../utils/8x8-chat'
import useIsMobile from '../../utils/hooks/useIsMobile'

import useStyles from './index.styles'

interface IProps {
  helpCategory?: HelpCategory
  supportLevel?: string | null
}

const isArmadilloEligible = (helpCategory: HelpCategory) => helpCategory === 'water_detected' || helpCategory === 'freeze_loss' || helpCategory === 'low_temp'

const ModalContent = ({ helpCategory, supportLevel }: IProps) => {
  const classes = useStyles
  const isMobile = useIsMobile()
  const { startChat } = useContext(ChatContext)
  const isFeatureFlagProviderReady = useWhenProviderReady()
  const is8x8ChatFlagEnabled = useBooleanFlagValue('8x8-chat-protect-web', false)
  const is8x8ChatEnabled = isFeatureFlagProviderReady && is8x8ChatFlagEnabled

  const [showFullDisclaimer, setShowFullDisclaimer] = useState(false)
  const [chatLoading, setChatLoading] = useState(false)
  const [chatAvailable, setChatAvailable] = useState(false)

  useEffect(() => {
    if (!is8x8ChatEnabled) {
      setChatLoading(false)
      setChatAvailable(false)
      return () => undefined
    }

    let isSubscribed = true
    setChatLoading(true)

    if (!document.getElementById(UNAUTHENTICATED_CHAT_BUTTON_CONTAINER_ID)) {
      const chatButtonContainer = document.createElement('div')
      chatButtonContainer.id = UNAUTHENTICATED_CHAT_BUTTON_CONTAINER_ID
      chatButtonContainer.style.display = 'none'
      document.body.appendChild(chatButtonContainer)
    }

    load8x8Chat(
      {
        channelName: UNAUTHENTICATED_CHAT_CHANNEL_NAME,
        channelUuid: UNAUTHENTICATED_CHAT_CHANNEL_UUID,
        scriptUuid: UNAUTHENTICATED_CHAT_SCRIPT_UUID,
        buttonContainerId: UNAUTHENTICATED_CHAT_BUTTON_CONTAINER_ID,
      },
      async () => {
        const available = await waitFor8x8ChatButton()

        if (!isSubscribed) return

        setChatAvailable(available)
        setChatLoading(false)
      },
      () => {
        if (!isSubscribed) return

        setChatAvailable(false)
        setChatLoading(false)
      }
    )

    return () => {
      isSubscribed = false
    }
  }, [is8x8ChatEnabled])

  const showArmadilloChatSupport = supportLevel === 'Third Party Support' && isArmadilloEligible(helpCategory ?? 'none')
  const supportNumberDisplay = showArmadilloChatSupport ? '860-722-1830' : '(844) 468-1866'
  const supportNumber = supportNumberDisplay.replace(/[ ()-]/g, '')

  // open phone dialer on mobile devices, do nothing on desktop
  const handlePhoneButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isMobile) {
      event.preventDefault()
      return
    }

    FullStory('trackEvent', { name: 'Armadillo_phone_tap', properties: { interface: 'web' } })
    window.location.href = `tel:+1${supportNumber}`
  }

  const handleChatButtonClick = () => {
    FullStory('trackEvent', { name: 'Armadillo_livechat_tap', properties: { interface: 'web' } })
  }

  if (showFullDisclaimer) {
    return (
      <Grid container sx={classes.contentContainer}>
        <Grid>
          <Typography variant="body2" sx={classes.disclaimerHeader}>
            <b>Third-Party Content Disclaimer</b>
          </Typography>

          <Typography variant="body2" sx={classes.disclaimerText}>
            The Leak Defense services, including the phone support and live chat, are provided by a third-party vendor and not by HSB or its affiliates. Neither
            HSB nor its affiliates are responsible for these services and HSB is only directing you to them for your convenience. By using these services, you
            acknowledge that HSB has no control over the third-party vendor(s) and no liability for any issues, damages, or losses that may arise from their use
            and you release HSB from any claims, damages, or losses. You use this content at your own risk. HSB does not endorse or guarantee the quality of
            these services. HSB's role is limited to providing information about the availability of these services, and we do not assume any responsibility for
            the actions or omissions of Leak Defense or any other third-party vendors.
          </Typography>
        </Grid>

        <Grid sx={classes.disclaimerButtonContainer}>
          <Button variant="contained" size="large" disableElevation onClick={() => setShowFullDisclaimer(false)} sx={classes.disclaimerCloseButton}>
            Close
          </Button>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container sx={classes.contentContainer}>
      <Grid>
        <Typography variant="h6" sx={classes.modalHeader}>
          {showArmadilloChatSupport ? 'Leak Defense Team' : 'Sensor Support Team'}
        </Typography>

        <Typography variant="body2" sx={classes.descriptionText}>
          {showArmadilloChatSupport ? (
            <>Contact our Leak Defense Team at any time 24/7 for help triaging a leak or freeze issue, including finding a plumber.</>
          ) : (
            <>
              For additional support, please contact the Sensor Support Team
              <br />
              Monday - Friday | 8am - 8pm ET
            </>
          )}
        </Typography>
      </Grid>

      <Grid container sx={classes.phoneRow}>
        <Grid>
          <Typography variant="body1">{supportNumberDisplay}</Typography>
        </Grid>

        <Grid sx={classes.phoneIconContainer}>
          {isMobile ? (
            <IconButton
              sx={classes.helpIconButton}
              onClick={handlePhoneButtonClick}
              aria-label={showArmadilloChatSupport ? 'Call Leak Defense Team' : 'Call Sensor Support Team'}
            >
              <PhoneOutlined sx={classes.phoneIcon} />
            </IconButton>
          ) : (
            <Box sx={classes.desktopPhoneIcon}>
              <PhoneOutlined sx={classes.phoneIcon} />
            </Box>
          )}
        </Grid>
      </Grid>

      {showArmadilloChatSupport ? (
        <>
          <Grid size={12}>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              component="a"
              href="https://info.armadillo.one/home-emergency"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleChatButtonClick}
              aria-label="Chat with Leak Defense Team"
              startIcon={<QuestionAnswerOutlined />}
              sx={classes.chatWithUsButton}
            >
              Chat with us
            </Button>
          </Grid>

          <Grid sx={classes.disclaimerContainer}>
            <Typography variant="body2" sx={classes.disclaimerBodyText}>
              By selecting to call or chat, you will be connected to a third-party service provider, whose content, services, and interactions are not
              controlled, endorsed, or guaranteed by HSB. Please see our{' '}
              <Link component="button" variant="body2" onClick={() => setShowFullDisclaimer(true)} sx={classes.disclaimerLink}>
                <b>Third-Party Content Disclaimer</b>
              </Link>{' '}
              for more details.
            </Typography>
          </Grid>
        </>
      ) : (
        <Grid container sx={classes.contactRow}>
          {is8x8ChatEnabled && !chatLoading && chatAvailable ? (
            <Grid size={12}>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                onClick={startChat}
                aria-label="Chat with Sensor Support Team"
                startIcon={<QuestionAnswerOutlined />}
                sx={classes.chatWithUsButton}
              >
                Chat with us
              </Button>
            </Grid>
          ) : is8x8ChatEnabled && chatLoading ? null : is8x8ChatEnabled ? (
            <Grid size={12}>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                component="a"
                href="http://resources.meshify.com/contact"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<EmailOutlined />}
                sx={classes.chatWithUsButton}
              >
                Contact Sensor Support
              </Button>
            </Grid>
          ) : (
            <>
              <Grid>
                <Typography variant="body1">sensorsupport@hsb.com</Typography>
              </Grid>

              <Grid sx={classes.phoneIconContainer}>
                <IconButton sx={classes.helpIconButton} component="a" href="mailto:sensorsupport@hsb.com" aria-label="Email Sensor Support Team">
                  <EmailOutlined sx={classes.phoneIcon} />
                </IconButton>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export default ModalContent
